import type { ClientTool } from "@/services/agentChat";
import { getScreenContext, type ScreenContext } from "./screenContext";

/**
 * Screen-tool registry (#675): UI-control tools a screen offers the agent while
 * it is mounted. Registration doubles as the route match — a tool is advertised
 * to the model only while its screen is on. Everything registered here changes
 * reversible view state only, never domain data, and executes in the browser,
 * so headless/scheduled turns can never see or call these tools.
 */

const registrations = new Set<readonly ClientTool[]>();

/**
 * Registers a screen's tools; returns the unregister function. Screens register
 * on mount and unregister on unmount, mirroring the screen-context provider.
 */
export function registerScreenTools(tools: readonly ClientTool[]): () => void {
  registrations.add(tools);
  return () => {
    registrations.delete(tools);
  };
}

/** Active screen tools; on a name collision the earliest registration wins. */
export function getScreenTools(): ClientTool[] {
  const byName = new Map<string, ClientTool>();
  for (const tools of registrations) {
    for (const tool of tools) {
      if (!byName.has(tool.name)) {
        byName.set(tool.name, tool);
      }
    }
  }
  return [...byName.values()];
}

const SETTLE_POLL_MS = 50;
const SETTLE_MAX_POLLS = 10;

/**
 * Reads the screen context once the UI has settled after a tool's action: polls
 * until the context differs from the pre-action snapshot (URL applied, provider
 * re-rendered), giving up after ~500ms and returning whatever is current. Result
 * counts may still be loading at that point — the context is honest about what
 * the screen shows right now.
 */
export async function captureScreenContextAfterSettle(
  before: ScreenContext,
): Promise<ScreenContext> {
  const read = () => getScreenContext(window.location.pathname, window.location.search);
  const beforeJson = JSON.stringify(before);
  for (let i = 0; i < SETTLE_MAX_POLLS; i++) {
    await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_MS));
    const next = read();
    if (JSON.stringify(next) !== beforeJson) {
      return next;
    }
  }
  return read();
}
